import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Check,
  Crown,
  Loader2,
  AlertCircle,
  Clock,
  ChevronRight,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  paymentService,
  type CreateOrderResponse,
  type RazorpayPaymentResponse,
  type SubscriptionPlan,
} from "../../services/payment.service";

// ═══════════════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════════════

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const [isYearly, setIsYearly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [razorpayConfig, setRazorpayConfig] = useState<{
    keyId: string;
    isConfigured: boolean;
  } | null>(null);

  // Load Razorpay script and config on mount
  useEffect(() => {
    const init = async () => {
      try {
        const [scriptLoaded, config, availablePlans] = await Promise.all([
          paymentService.loadRazorpayScript(),
          paymentService.getRazorpayConfig(),
          paymentService.listSubscriptionPlans(),
        ]);
        setRazorpayLoaded(scriptLoaded);
        setRazorpayConfig(config);
        setPlans(availablePlans);
      } catch (error) {
        console.error("Failed to initialize payment:", error);
        setPlansError(
          "Subscription plans are unavailable. Please contact support.",
        );
      } finally {
        setPlansLoading(false);
      }
    };
    init();
  }, []);

  const handleSelectPlan = async (planId: string) => {
    if (!razorpayConfig?.isConfigured) {
      toast.error("Payment gateway not configured. Please contact support.");
      return;
    }

    if (!razorpayLoaded) {
      toast.error("Payment system loading. Please try again.");
      return;
    }

    setSelectedPlan(planId);
    setLoading(true);

    try {
      const plan = plans.find((p) => p.id === planId);
      if (!plan) throw new Error("Plan not found");

      const amount = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
      if (amount === null) {
        throw new Error(
          `${plan.name} does not support ${isYearly ? "yearly" : "monthly"} billing`,
        );
      }

      const order = await paymentService.createSubscriptionOrder(
        plan.id,
        isYearly ? "yearly" : "monthly",
      );

      // Open Razorpay checkout
      openRazorpayCheckout(order, plan.name);
    } catch (error: any) {
      console.error("Payment error:", error);
      toast.error(
        error?.response?.data?.message || "Failed to initiate payment",
      );
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const openRazorpayCheckout = (
    order: CreateOrderResponse,
    planName: string,
  ) => {
    paymentService.openCheckout({
      key: order.keyId,
      amount: order.amount,
      currency: order.currency,
      name: "MediCorePay",
      description: `${planName} - ${isYearly ? "Yearly" : "Monthly"} Plan`,
      order_id: order.orderId,
      prefill: order.prefill,
      notes: order.notes,
      theme: {
        color: "#4F46E5",
      },
      handler: async (response: RazorpayPaymentResponse) => {
        await handlePaymentSuccess(response);
      },
      modal: {
        ondismiss: () => {
          setLoading(false);
          setSelectedPlan(null);
        },
      },
    });
  };

  const handlePaymentSuccess = async (response: RazorpayPaymentResponse) => {
    try {
      const result = await paymentService.verifySubscriptionPayment({
        razorpayOrderId: response.razorpay_order_id,
        razorpayPaymentId: response.razorpay_payment_id,
        razorpaySignature: response.razorpay_signature,
      });

      if (result.success) {
        toast.success("Payment successful! Your subscription is now active.");
        navigate("/subscription/success");
      } else {
        toast.error(result.message || "Payment verification failed");
      }
    } catch (error: any) {
      console.error("Verification error:", error);
      toast.error("Payment verification failed. Please contact support.");
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center">
          <Crown className="mx-auto h-12 w-12 text-indigo-600" />
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Choose Your Plan
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-lg text-gray-600">
            Select the plan and billing interval that best fit your pharmacy.
            Pricing is loaded securely from your MediCorePay account.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="mt-8 flex justify-center">
          <div className="relative flex bg-gray-100 rounded-lg p-1">
            <button
              type="button"
              className={`relative py-2 px-4 rounded-md text-sm font-medium transition-all ${
                !isYearly
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setIsYearly(false)}
            >
              Monthly
            </button>
            <button
              type="button"
              className={`relative py-2 px-4 rounded-md text-sm font-medium transition-all ${
                isYearly
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setIsYearly(true)}
            >
              Yearly
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="mt-12 grid gap-8 lg:grid-cols-3">
          {plansLoading && (
            <div className="col-span-full flex justify-center py-16">
              <Loader2 className="h-7 w-7 animate-spin text-indigo-600" />
            </div>
          )}
          {plansError && (
            <div className="col-span-full rounded-xl border border-amber-200 bg-amber-50 p-4 text-center text-sm text-amber-800">
              {plansError}
            </div>
          )}
          {plans.map((plan) => {
            const price = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
            const isSelected = selectedPlan === plan.id;
            const isPopular =
              plan.type === "GROWTH" ||
              plan.name.toLowerCase().includes("growth");

            return (
              <div
                key={plan.id}
                className={`relative flex flex-col rounded-2xl border-2 bg-white p-8 shadow-sm transition-all ${
                  isPopular
                    ? "border-indigo-600 ring-2 ring-indigo-600"
                    : "border-gray-200 hover:border-indigo-300"
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center rounded-full bg-indigo-600 px-4 py-1 text-sm font-semibold text-white">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {plan.name}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {plan.description ?? "Subscription plan"}
                  </p>
                </div>

                <div className="mb-6">
                  <p className="flex items-baseline">
                    <span className="text-4xl font-bold text-gray-900">
                      {price === null
                        ? "Unavailable"
                        : `₹${price.toLocaleString("en-IN")}`}
                    </span>
                    {price !== null && (
                      <span className="ml-2 text-gray-500">
                        /{isYearly ? "year" : "month"}
                      </span>
                    )}
                  </p>
                  {isYearly && price !== null && (
                    <p className="mt-1 text-sm text-gray-500">
                      ₹{Math.round(price / 12).toLocaleString("en-IN")}/month
                      billed annually
                    </p>
                  )}
                </div>

                <ul className="mb-8 flex-1 space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start">
                      <Check className="mr-3 h-5 w-5 flex-shrink-0 text-indigo-600" />
                      <span className="text-sm text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={loading || price === null}
                  className={`w-full rounded-lg py-3 px-4 text-sm font-semibold transition-all ${
                    isPopular
                      ? "bg-indigo-600 text-white hover:bg-indigo-700"
                      : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                  } ${
                    (loading && isSelected) || price === null
                      ? "cursor-not-allowed opacity-75"
                      : ""
                  }`}
                >
                  {loading && isSelected ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </span>
                  ) : (
                    `Get Started with ${plan.name}`
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Gateway Notice */}
        {!razorpayConfig?.isConfigured && (
          <div className="mt-8 flex items-center justify-center gap-2 text-amber-600">
            <AlertCircle className="h-5 w-5" />
            <span className="text-sm">
              Payment gateway is not configured. Please contact support.
            </span>
          </div>
        )}

        {/* Payment History Link */}
        <div className="mt-12 text-center">
          <button
            onClick={() => navigate("/subscription/history")}
            className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-700"
          >
            <Clock className="mr-2 h-4 w-4" />
            View Payment History
            <ChevronRight className="ml-1 h-4 w-4" />
          </button>
        </div>

        {/* FAQ Section */}
        <div className="mt-16 border-t border-gray-200 pt-12">
          <h3 className="text-xl font-semibold text-gray-900 text-center">
            Frequently Asked Questions
          </h3>
          <dl className="mt-8 grid gap-6 lg:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-900">
                Can I change my plan later?
              </dt>
              <dd className="mt-2 text-sm text-gray-500">
                Yes. The API verifies that your current store and user counts
                fit the selected plan before creating an order.
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-900">
                What payment methods are accepted?
              </dt>
              <dd className="mt-2 text-sm text-gray-500">
                We accept all major credit/debit cards, UPI, net banking, and
                digital wallets via Razorpay.
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-900">
                Is there a free trial?
              </dt>
              <dd className="mt-2 text-sm text-gray-500">
                Trial availability depends on the selected plan and your account
                history.
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-900">
                Can I cancel anytime?
              </dt>
              <dd className="mt-2 text-sm text-gray-500">
                Yes, you can cancel your subscription at any time. You'll
                continue to have access until the end of your billing period.
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}
